import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { judgeService, eventService, appService, judgeInvitationService, judgeConfigService } from '../services/supabaseService';
import { 
  judgeServiceProxy, 
  judgeConfigServiceProxy, 
  judgeInvitationServiceProxy,
  storageServiceProxy
} from '../services/proxyService';
import { useNotifications } from '../components/shared/useNotifications';
import { useAuth } from '../context/AuthContext';
import { DISCIPLINE_TABS } from '../utils/constants';
import { getApparatusForEvent } from '../utils/disciplineUtils';
import JudgeProfileEditor from '../components/judge/JudgeProfileEditor';
import CategoryFooter from '../components/shared/CategoryFooter';
import ClubTopbar from '../components/club/ClubTopbar';
import './JudgeDashboard.css';
import { Calendar, LogOut, ClipboardList, ChevronLeft, ChevronRight, KeyRound, CheckCircle, AlertCircle, Lock, Menu, X, User, MapPin } from 'lucide-react';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const getDisciplineDisplay = (discipline, category) => {
  if (!discipline) return { fullName: 'General', logo: null, name: '' };
  const match = DISCIPLINE_TABS.find(t =>
    t.discipline === discipline && (t.category === (category || '') || (!category && !t.category))
  );
  return match || { fullName: discipline, logo: null, name: '' };
};

const JudgeDashboard = () => {
  const navigate = useNavigate();
  const { success: notifySuccess, error: notifyError, warning: notifyWarning } = useNotifications();
  const { profile, signOut, isAuthenticated, loading: authLoading, updatePassword } = useAuth();
  
  const [judgeInfo, setJudgeInfo] = useState(null);
  const [assignedEvents, setAssignedEvents] = useState([]);
  const [eventImages, setEventImages] = useState({});
  const [eventConfigs, setEventConfigs] = useState({}); // {eventId: [{apparatus, judge_role, ...}]}
  const [loading, setLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('events');

  // Month selector state
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  // Invitation code state
  const [invitationCode, setInvitationCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeSuccess, setCodeSuccess] = useState('');
  const [codeError, setCodeError] = useState('');

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Judge photo for sidebar
  const [judgePhotoUrl, setJudgePhotoUrl] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoadingTimeout(false);
    const timeout = setTimeout(() => {
      setLoadingTimeout(true);
    }, 10000);

    const judgeId = profile.id;

    // Cargar información del juez
    const loadJudgeData = async () => {
      try {
        console.log('📥 Cargando datos del juez...');
        let { data: judge, error } = await judgeServiceProxy.getById(judgeId);
        
        if (error) {
          console.error('❌ Error al cargar juez:', error);
          const errorMsg = error.message || 'No se pudo cargar tu información';
          notifyError(errorMsg);
          setLoading(false);
          return;
        }

        if (!judge) {
          // Crear registro de judge si no existe
          const newJudge = { id: judgeId, name: profile.name || 'Juez', email: profile.email };
          const { data: created, error: createError } = await judgeService.create(newJudge, true, true);
          if (createError) {
            console.error('Error creando judge:', createError);
            notifyError('Tu perfil no fue encontrado en el sistema.');
            setLoading(false);
            return;
          }
          judge = created;
        }

        console.log('✅ Juez cargado:', judge.name);
        setJudgeInfo(judge);

        // Cargar foto del juez para el sidebar
        if (judge.photo_url) {
          setJudgePhotoUrl(judge.photo_url);
        } else {
          try {
            const { data: photoUrl } = await storageServiceProxy.getJudgePhoto(judgeId);
            if (photoUrl) setJudgePhotoUrl(photoUrl);
          } catch (e) { /* ignore */ }
        }

        // Cargar eventos asignados al juez
        console.log('📅 Cargando eventos asignados...');
        const { data: events, error: eventsError } = await judgeServiceProxy.getEventsByJudge(judgeId);
        
        if (eventsError) {
          console.warn('⚠️ Error cargando eventos:', eventsError);
          // No es un error fatal - continuar sin eventos
        }
        
        if (events && events.length > 0) {
          console.log(`✅ ${events.length} evento(s) cargado(s)`);
          setAssignedEvents(events);
          
          // Cargar imágenes de eventos usando el proxy
          const images = {};
          for (const event of events) {
            try {
              const { data: imgUrl } = await storageServiceProxy.getEventImage(event.id);
              if (imgUrl) images[event.id] = imgUrl;
            } catch (e) { /* ignore */ }
          }
          
          setEventImages(images);

          // Cargar configuraciones del juez por evento
          const configs = {};
          for (const event of events) {
            try {
              const { data: cfgs } = await judgeConfigServiceProxy.getConfigsByEvent(judgeId, event.id);
              if (cfgs && cfgs.length > 0) {
                configs[event.id] = cfgs;
              }
            } catch (_) {}
          }
          setEventConfigs(configs);
        } else {
          console.log('ℹ️ Sin eventos asignados');
          setAssignedEvents([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('❌ Error en loadJudgeData:', err);
        notifyError('Error al cargar los datos. Intenta recargar la página.');
        setLoading(false);
      }
    };

    loadJudgeData();
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, profile?.id]);

  // Filter events by selected month/year
  const filteredEvents = useMemo(() => {
    return assignedEvents.filter(event => {
      if (!event.date) return false;
      const eventDate = new Date(event.date + 'T00:00:00');
      return eventDate.getMonth() === selectedMonth && eventDate.getFullYear() === selectedYear;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [assignedEvents, selectedMonth, selectedYear]);

  // Check if event is in the past (read-only)
  const isEventPast = (eventDate) => {
    const evDate = new Date(eventDate + 'T23:59:59');
    return evDate < new Date();
  };

  // Navigate months
  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(y => y - 1);
    } else {
      setSelectedMonth(m => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(y => y + 1);
    } else {
      setSelectedMonth(m => m + 1);
    }
  };

  const handleGoToCurrentMonth = () => {
    setSelectedMonth(today.getMonth());
    setSelectedYear(today.getFullYear());
  };

  // Handle invitation code submission
  const handleSubmitCode = async () => {
    setCodeError('');
    setCodeSuccess('');

    if (!invitationCode.trim()) {
      setCodeError('Ingresa un código de invitación');
      return;
    }

    setCodeLoading(true);
    try {
      const judgeId = profile?.id;
      if (!judgeId) {
        setCodeError('Sesión no válida. Vuelve a iniciar sesión.');
        setCodeLoading(false);
        return;
      }

      // Validate the invitation code
      const { data: invitation, error: valError } = await judgeInvitationServiceProxy.validateInvitation(invitationCode.trim());
      
      if (valError) {
        const errMsg = valError.message || valError || 'Código inválido o expirado';
        setCodeError(errMsg);
        setCodeLoading(false);
        return;
      }

      if (!invitation) {
        setCodeError('Código de invitación no encontrado');
        setCodeLoading(false);
        return;
      }

      // Mark invitation as used
      await judgeInvitationServiceProxy.markInvitationAsUsed(invitation.id, judgeId);

      // Add judge to event
      await judgeServiceProxy.addToEvent(invitation.event_id, judgeId);

      // Reload events
      const { data: events } = await judgeServiceProxy.getEventsByJudge(judgeId);
      if (events) {
        setAssignedEvents(events);
      }

      setCodeSuccess('✅ ¡Acceso concedido! La copa ha sido agregada a tu lista.');
      setInvitationCode('');
      notifySuccess('Copa agregada exitosamente');

      // Clear success after a few seconds
      setTimeout(() => setCodeSuccess(''), 5000);
    } catch (err) {
      console.error('Error procesando código:', err);
      setCodeError('Error al procesar el código. Intenta nuevamente.');
    } finally {
      setCodeLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/jueces');
    notifySuccess('Sesión cerrada');
  };

  if (loading) {
    return (
      <div className="jd-loading-state">
        <div className="spinner"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  if (!judgeInfo) {
    if (loadingTimeout) {
      return (
        <div className="jd-loading-state">
          <p>No se pudo cargar la información del juez</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn btn-judge-primary"
            style={{ marginTop: '16px' }}
          >
            Recargar página
          </button>
        </div>
      );
    }
    return (
      <div className="jd-loading-state">
        <div className="spinner"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  const SIDEBAR_ITEMS = [
    { key: 'events', label: 'Mis Copas', icon: Calendar, color: '#3b82f6' },
    { key: 'invitation', label: 'Código Invitación', icon: KeyRound, color: '#8b5cf6' },
    { key: 'profile', label: 'Mi Perfil', icon: User, color: '#10b981' },
  ];

  const goTo = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };

  return (
    <div className="jd-layout">
      {/* ========== SIDEBAR ========== */}
      <button className="jd-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>
      {sidebarOpen && <div className="jd-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`jd-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="jd-sidebar-brand">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="jd-sidebar-logo-img" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        </div>

        <nav className="jd-sidebar-nav">
          {SIDEBAR_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.key;
            return (
              <button key={item.key} className={`jd-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => goTo(item.key)} style={{ '--item-color': item.color }}>
                <span className="jd-nav-icon"><Icon size={20} /></span>
                <span className="jd-nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="jd-sidebar-footer">
          <div className="jd-user-card">
            <div className="jd-user-avatar">
              {judgePhotoUrl ? (
                <img src={judgePhotoUrl} alt={judgeInfo?.name} className="jd-user-avatar-img" />
              ) : (
                judgeInfo?.name?.[0]?.toUpperCase() || 'J'
              )}
            </div>
            <div className="jd-user-info">
              <p className="jd-user-name">{judgeInfo?.name || 'Juez'}</p>
              <p className="jd-user-role">Juez</p>
            </div>
          </div>
          <button className="jd-sidebar-logout" onClick={handleLogout}>
            <LogOut size={18} /><span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* ========== MAIN CONTENT ========== */}
      <main className="jd-main">
        <ClubTopbar
          pageTitle={SIDEBAR_ITEMS.find(s => s.key === activeSection)?.label || 'Panel'}
          onToggleSidebar={() => setSidebarOpen(o => !o)}
          clubName={judgeInfo?.name || ''}
          clubLogoUrl={judgePhotoUrl || null}
          initials={(judgeInfo?.name?.[0] || 'J').toUpperCase()}
          role="Juez"
          roleLabel="Cuenta de juez"
          showSettings={false}
          onGoProfile={() => { setActiveSection('profile'); setSidebarOpen(false); }}
          onLogout={handleLogout}
          notificationsCount={0}
        />

        <div className="jd-content">
          {/* ===== INVITATION CODE SECTION ===== */}
          {activeSection === 'invitation' && (
            <>
              <p className="jd-page-subtitle"><KeyRound size={16} />Ingresa el código que recibiste por correo para unirte a una copa como juez.</p>
            <div className="jd-card">
              <div className="jd-card-header">
                <h2><KeyRound size={20} style={{ color: '#8b5cf6' }} /> Código de acceso</h2>
              </div>
              <div className="jd-card-body">
                <p className="jd-code-desc">Si recibiste un código de invitación por correo, ingrésalo aquí para acceder a la copa.</p>
                <div className="jd-code-input-row">
                  <input
                    type="text"
                    className="jd-code-input"
                    value={invitationCode}
                    onChange={(e) => { setInvitationCode(e.target.value.toUpperCase()); setCodeError(''); setCodeSuccess(''); }}
                    placeholder="Ej: FROENEU3X5"
                    maxLength={12}
                    disabled={codeLoading}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmitCode()}
                  />
                  <button
                    className="btn btn-judge-primary jd-code-btn"
                    onClick={handleSubmitCode}
                    disabled={codeLoading || !invitationCode.trim()}
                  >
                    {codeLoading ? 'Verificando...' : 'Validar Código'}
                  </button>
                </div>
                {codeError && (
                  <div className="jd-code-alert error">
                    <AlertCircle size={16} />
                    <span>{codeError}</span>
                  </div>
                )}
                {codeSuccess && (
                  <div className="jd-code-alert success">
                    <CheckCircle size={16} />
                    <span>{codeSuccess}</span>
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* ===== EVENTS SECTION WITH MONTH SELECTOR ===== */}
          {activeSection === 'events' && (
            <>
              <p className="jd-page-subtitle"><Calendar size={16} />Copas donde estás asignado como juez. Configura tus aparatos antes de que inicien.</p>
            <div className="jd-card">
              <div className="jd-card-header">
                <h2><Calendar size={20} style={{ color: '#3b82f6' }} /> Listado de copas</h2>
              </div>
              <div className="jd-card-body">
                {/* Month Navigation */}
                <div className="jd-month-nav">
                  <button className="jd-month-btn" onClick={handlePrevMonth} title="Mes anterior">
                    <ChevronLeft size={20} />
                  </button>
                  <div className="jd-month-label">
                    <span className="jd-month-name">{MONTH_NAMES[selectedMonth]}</span>
                    <span className="jd-month-year">{selectedYear}</span>
                  </div>
                  <button className="jd-month-btn" onClick={handleNextMonth} title="Mes siguiente">
                    <ChevronRight size={20} />
                  </button>
                  {(selectedMonth !== today.getMonth() || selectedYear !== today.getFullYear()) && (
                    <button className="jd-month-today" onClick={handleGoToCurrentMonth}>
                      Hoy
                    </button>
                  )}
                  <span className="jd-month-count">
                    {filteredEvents.length} copa{filteredEvents.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {filteredEvents.length > 0 ? (
                  <div className="events-grid">
                    {filteredEvents.map((event) => {
                      const isPast = isEventPast(event.date);
                      const disc = getDisciplineDisplay(event.discipline, event.category);
                      const configs = eventConfigs[event.id];
                      const isConfigured = configs && configs.length > 0;
                      return (
                        <div key={event.id} className={`event-card ${isPast ? 'event-card-past' : ''}`}>
                          {/* Image or Discipline Banner */}
                          {eventImages[event.id] ? (
                            <div className="event-card-image">
                              <img 
                                src={eventImages[event.id]} 
                                alt={event.name}
                                className="event-image"
                              />
                              {isPast && (
                                <div className="event-past-overlay">
                                  <Lock size={20} />
                                  <span>Finalizado</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="event-card-image" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {disc.logo ? (
                                <img src={disc.logo} alt={disc.name} style={{ width: 40, height: 40, objectFit: 'contain', opacity: 0.7 }} />
                              ) : (
                                <Calendar size={32} style={{ color: '#c4b5fd', opacity: 0.5 }} />
                              )}
                              {isPast && (
                                <div className="event-past-overlay">
                                  <Lock size={20} />
                                  <span>Finalizado</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Card Info - unified */}
                          <div className="event-card-content">
                            <h3>{event.name}</h3>
                            <div className="event-detail">
                              <Calendar size={11} />
                              <span>
                                {new Date(event.date + 'T00:00:00').toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              {isPast && <span className="event-badge-past">Historial</span>}
                            </div>
                            {event.location && (
                              <div className="event-detail">
                                <MapPin size={11} />
                                <span>{event.location}</span>
                              </div>
                            )}
                            {/* Discipline badge */}
                            <div className="jd-discipline-badge">
                              {disc.logo && <img src={disc.logo} alt={disc.name} className="jd-discipline-logo" />}
                              <div>
                                <span className="jd-discipline-name">{disc.name || 'General'}</span>
                                <span className="jd-discipline-full">{disc.fullName}</span>
                              </div>
                            </div>
                            
                            {/* Config status */}
                            {isConfigured ? (
                              <div className="jd-config-status jd-config-ready">
                                <CheckCircle size={12} />
                                <div className="jd-config-details">
                                  <span className="jd-config-label">Configurado</span>
                                  <span className="jd-config-apparatus">
                                    {(() => {
                                      const appList = getApparatusForEvent(event);
                                      const appNames = configs.map(c => {
                                        const match = appList.find(a => a.code === c.apparatus);
                                        return match ? match.name : c.apparatus;
                                      });
                                      const templateLabel = configs[0]?.template_type === 'custom' 
                                        ? 'Config. Personalizada' 
                                        : 'Plantilla FIG';
                                      return `${appNames.join(', ')} — ${templateLabel}`;
                                    })()}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="jd-config-status jd-config-pending">
                                <AlertCircle size={12} />
                                <span>Sin configurar</span>
                              </div>
                            )}
                          </div>
                          <div className="event-card-footer">
                            {isPast ? (
                              <button 
                                className="btn-score-entry btn-score-readonly"
                                onClick={() => navigate(`/jueces/scores/${event.id}`)}
                              >
                                <ClipboardList size={16} />
                                Ver Puntajes (Solo lectura)
                              </button>
                            ) : (
                              <button 
                                className={`btn-score-entry ${isConfigured ? '' : 'btn-score-configure'}`}
                                onClick={() => navigate(`/jueces/scores/${event.id}`)}
                              >
                                <ClipboardList size={16} />
                                {isConfigured ? 'Ingresar Puntajes' : 'Configurar y Puntuar'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-state">
                    <Calendar size={48} />
                    <p>No tienes copas en {MONTH_NAMES[selectedMonth]} {selectedYear}</p>
                    <p className="subtitle">Navega entre meses para ver tu historial o copas futuras</p>
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* ===== PROFILE SECTION ===== */}
          {activeSection === 'profile' && judgeInfo && (
            <>
              <p className="jd-page-subtitle"><User size={16} />Actualiza tus datos personales, categorías, disciplinas habilitadas y foto de perfil.</p>
            <JudgeProfileEditor 
              judgeInfo={judgeInfo}
              onUpdate={(updatedInfo) => {
                setJudgeInfo(updatedInfo);
                if (updatedInfo.photo_url) {
                  setJudgePhotoUrl(updatedInfo.photo_url);
                }
              }}
            />
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default JudgeDashboard;
